import React, { useEffect, useState } from "react";
import ControlPanelSystem from "src/components/controlPanelSystem/ControlPanelSystem";
import { useCallback } from "react";
import useControlPanel from "src/hooks/useControlPanel";
import { useMemo } from "react";
import Pagination from "src/components/Pagination/Pagination";
import { dataStateType } from "src/constants/status";
import useCurrentActionCache from "src/hooks/useCurrentActionCache";
import { CACHE_NOTIFY_CONFIG } from "src/constants/actionCache";
import { NotifyConfirm } from "src/components/notify/Notify";
import { path } from "src/constants/path";
import {
  createFLFNotifyConfig,
  getFLFNotifyConfig,
  removeFLFNotifyConfig,
  updateFLFNotifyConfig,
} from "src/apis/FLF/notifyConfig.api";
import NotifyConfigTable from "../../components/Table/NotifyConfigTable";
import FLFNotifyConfigControlPanel from "../../components/ControlPanel/FLFNotifyConfigControlPanel";

const initNotifyConfigData = {
  list: [],
  total_record: 0,
};

const FLFNotifyConfig = () => {
  const {
    page,
    searchKey,
    numberPage,
    setNumberPage,
    limit,
    setLimit,
    status,
    setStatus,
    startIndex,
    endIndex,
    setStartDate,
    setEndDate,
    handleInputChange,
    handleChangePage,
  } = useControlPanel();
  const { currentActionCache, setCurrentActionCache } = useCurrentActionCache();
  const [notifyConfigData, setNotifyConfigData] =
    useState(initNotifyConfigData);
  const [filterNotifyConfigs, setFilterNotifyConfigs] = useState([]);
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [errorsForm, setErrorsForm] = useState({});

  const fetchNotifyConfig = useCallback(() => {
    getFLFNotifyConfig({}, (result) => {
      setNotifyConfigData({ ...result });
      setFilterNotifyConfigs([...result.list]);
    });
  }, []);

  useEffect(() => {
    let isCancelled = false;
    if (!isCancelled) {
      fetchNotifyConfig();
    }

    return () => {
      // cancel the request before component unmounts
      isCancelled = true;
    };
  }, [fetchNotifyConfig]);

  useEffect(() => {
    setNumberPage(page);
  }, [page, setNumberPage]);

  const handleOpenPopup = useCallback((isOpen, item = null) => {
    setOpenPopup(isOpen);
    setRecordForEdit(item);
    setErrorsForm({});
  }, []);

  const updateFilteredNotifyConfig = useCallback((notifyConfigs) => {
    setFilterNotifyConfigs(notifyConfigs);
  }, []);

  /* ---- handle search ----- */
  const searchByKeyWord = useCallback(
    (notifyConfigs, key) => {
      const search = searchKey.toLowerCase().trim();
      if (status === dataStateType.INACTIVE) {
        return [];
      }
      return notifyConfigs.filter((notifyConfig) => {
        const valueType = (notifyConfig.valueType || "")
          .toString()
          .toLowerCase();
        const attrCode = (notifyConfig.attrCode || "").toString().toLowerCase();
        const content = (notifyConfig.content || "").toString().toLowerCase();

        return (
          valueType.indexOf(search) !== -1 ||
          attrCode.indexOf(search) !== -1 ||
          content.indexOf(search) !== -1
        );
      });
    },
    [searchKey, status]
  );

  const handleSearch = useCallback(
    (value) => {
      const filteredNotifyConfig = value
        ? searchByKeyWord(notifyConfigData.list, value)
        : notifyConfigData.list;
      updateFilteredNotifyConfig(filteredNotifyConfig);
    },
    [notifyConfigData.list, searchByKeyWord, updateFilteredNotifyConfig]
  );

  useEffect(() => {
    handleSearch(searchKey);
  }, [searchKey, handleSearch]);

  /* ---- handle add new and update ----- */
  const addOrEdit = useCallback(
    (notifyConfig, resetForm) => {
      if (notifyConfig.id) {
        updateFLFNotifyConfig(notifyConfig.id, notifyConfig, handleApiResponse);
      } else {
        createFLFNotifyConfig(notifyConfig, handleApiResponse);
      }

      function handleApiResponse(result) {
        if (result.data?.status === 422) {
          setErrorsForm(result.data.data);
        } else {
          fetchNotifyConfig();
          resetForm();
          setRecordForEdit(null);
          setOpenPopup(false);
          setErrorsForm({});
          setCurrentActionCache(CACHE_NOTIFY_CONFIG);
        }
      }
    },
    [fetchNotifyConfig, setCurrentActionCache]
  );

  /* ---- handle sort and DnD ----- */

  const handleDelete = (notifyConfigDelete) => {
    NotifyConfirm("warning", "Are you delete it ?", (result) => {
      if (result.isConfirmed) {
        removeFLFNotifyConfig(
          notifyConfigDelete.id,
          setCurrentActionCache(CACHE_NOTIFY_CONFIG)
        );
        setNotifyConfigData((prev) => {
          return {
            ...prev,
            list: prev.list.filter(
              (notifyConfig) => notifyConfig.id !== notifyConfigDelete.id
            ),
            total_record: prev.total_record - 1,
          };
        });

        updateFilteredNotifyConfig((prev) =>
          prev.filter(
            (notifyConfig) => notifyConfig.id !== notifyConfigDelete.id
          )
        );
      }
    });
  };

  const resultFilter = useMemo(
    () => searchByKeyWord(filterNotifyConfigs, searchKey),
    [filterNotifyConfigs, searchByKeyWord, searchKey]
  );
  const listItems = useMemo(
    () => resultFilter.slice(startIndex, endIndex),
    [resultFilter, startIndex, endIndex]
  );

  return (
    <div>
      <div className="page-header">
        <h2>Notify Config</h2>
      </div>

      {/** control pan */}
      <FLFNotifyConfigControlPanel
        handleOpenPopup={handleOpenPopup}
        currentActionCache={currentActionCache}
        recordForEdit={recordForEdit}
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
        addOrEdit={addOrEdit}
        errorsForm={errorsForm}
      >
        <ControlPanelSystem
          searchKey={searchKey}
          handleInputChange={(e) => handleInputChange(e, handleSearch)}
          limit={limit}
          setLimit={setLimit}
          setNumberPage={setNumberPage}
          status={status}
          setStatus={setStatus}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          isDisplayDatePicker={false}
        />
      </FLFNotifyConfigControlPanel>

      {/** table */}
      <NotifyConfigTable
        data={listItems}
        handleOpenPopup={handleOpenPopup}
        handleDelete={handleDelete}
        status={dataStateType.ACTIVE}
      />

      {/** Pagination */}
      <Pagination
        pathname={path.flfNotifyConfig}
        page={numberPage}
        handleChangePage={(page) => handleChangePage(page)}
        limit={limit}
        pageSize={resultFilter.length}
      />
    </div>
  );
};

export default FLFNotifyConfig;
